import {
	useMutation,
	useQueryClient,
	useQuery,
} from '@tanstack/react-query'
import { useVenti } from 'venti'
import {
	EntityType,
	SiteConfiguration,
	SiteConfigurationWithInherited,
} from '@matech/thebigpos-sdk'
import { AxiosResponse } from 'axios'
import { useIntl } from 'react-intl'
import queryKeys from '../services/queryKeys'
import { TheBigPOSApi } from '../lib/TheBigPOSClient'
import { getTheme } from '../config'
import { useAlert } from './index'

const theme = getTheme()

export type SiteConfigurationMapItem = {
	storageKey: string
	queryKey: string
	queryFn: (
		entryId: string,
		siteConfigurationId: string
	) => Promise<AxiosResponse<SiteConfigurationWithInherited>>
	mutationFn: (
		entryId: string,
		siteConfigurationId: string,
		data: SiteConfiguration
	) => Promise<AxiosResponse<SiteConfiguration>>
}

export type UseSiteConfigurationByEntityTypeProps = {
	entityId: string
	entityType: Exclude<EntityType, 'Account'>
}

const siteConfigurationMap: Record<
	Exclude<EntityType, 'Account'>,
	SiteConfigurationMapItem
> = {
	Branch: {
		storageKey: theme.storageKeys.editingBranchSiteConfigurationId,
		queryKey: queryKeys.branchSiteConfiguration,
		queryFn: TheBigPOSApi.getBranchSiteConfiguration,
		mutationFn: TheBigPOSApi.replaceBranchSiteConfiguration,
	},
	Corporate: {
		storageKey: theme.storageKeys.editingCorporateSiteConfigurationId,
		queryKey: queryKeys.corporationSiteConfiguration,
		queryFn: TheBigPOSApi.getCorporateSiteConfiguration,
		mutationFn: TheBigPOSApi.replaceCorporateSiteConfiguration,
	},
	LoanOfficer: {
		storageKey:
			theme.storageKeys.editingLoanOfficerSiteConfigurationId,
		queryKey: queryKeys.loanOfficerSiteConfiguration,
		queryFn: TheBigPOSApi.getLoanOfficerSiteConfiguration,
		mutationFn: TheBigPOSApi.replaceLoanOfficerSiteConfiguration,
	},
	Realtor: {
		storageKey: theme.storageKeys.editingPartnerSiteConfigurationId,
		queryKey: queryKeys.partnerSiteConfiguration,
		queryFn: TheBigPOSApi.getPartnerSiteConfiguration,
		mutationFn: TheBigPOSApi.replacePartnerSiteConfiguration,
	},
}

const useSiteConfigurationByEntity = ({
	entityId,
	entityType,
}: UseSiteConfigurationByEntityTypeProps) => {
	const ventiState = useVenti()
	const { alert } = useAlert()
	const queryClient = useQueryClient()
	const { formatMessage } = useIntl()

	const siteConfigurationId = siteConfigurationMap[entityType]
		?.storageKey
		? ventiState.get(siteConfigurationMap[entityType].storageKey)
		: ''

	const isNew = entityId === 'new'
	const isEnabled = !!entityId && !isNew && !!siteConfigurationId

	const getUseQueryParameters = () => {
		if (!siteConfigurationMap[entityType] || !isEnabled) {
			return {
				queryKey: [],
				queryFn: undefined,
				enabled: false,
			}
		}

		return {
			queryKey: [
				siteConfigurationMap[entityType]?.queryKey,
				siteConfigurationId,
			],
			queryFn: () =>
				siteConfigurationMap[entityType].queryFn(
					entityId,
					siteConfigurationId
				),
			retry: 2,
			refetchOnWindowFocus: false,
			select: (data: AxiosResponse<SiteConfigurationWithInherited>) =>
				data.data,
		}
	}

	const getMutationParameters = () => {
		if (!siteConfigurationMap[entityType] || !isEnabled) {
			return {
				mutationFn: undefined,
				onSuccess: undefined,
				onError: undefined,
			}
		}

		return {
			mutationFn: (data: SiteConfiguration) =>
				siteConfigurationMap[entityType].mutationFn(
					entityId,
					siteConfigurationId,
					data
				),
			onSuccess: (
				data: AxiosResponse<SiteConfiguration>,
				variables: SiteConfiguration
			) => {
				queryClient.setQueryData(
					[
						siteConfigurationMap[entityType]?.queryKey,
						siteConfigurationId,
					],
					(oldData?: SiteConfigurationWithInherited) => {
						if (!oldData) return undefined
						return {
							...oldData,
							siteConfiguration: {
								...oldData.siteConfiguration,
								...variables,
							},
						}
					}
				)
				alert(
					formatMessage({ id: 'site_configuration.update_success' }),
					{
						severity: 'success',
					}
				)
			},
			onError: () => {
				alert(
					formatMessage({ id: 'site_configuration.update_error' }),
					{
						severity: 'error',
					}
				)
			},
		}
	}
	const query = useQuery({
		...getUseQueryParameters(),
	})

	const update = useMutation({
		...getMutationParameters(),
	})

	return {
		query,
		update,
	}
}

export default useSiteConfigurationByEntity
